// @flow
import React from 'react';
import { FormattedMessage, FormattedNumber, IntlProvider } from 'react-intl';
import type { IntlShape } from 'react-intl';
import messages from './messages';

const {
  yes,
  no,
  areaMetric,
  currencyPrint,
  dateRecent,
  dateOldest,
  priceHighFirst,
  priceLowFirst,
  mileage,
  priceAreaHighFirst,
  priceAreaLowFirst,
  relevance,
  timeSinceMoreMonth,
  timeSinceSince,
  timeHourOne,
  timeHourOther,
  timeMinuteOne,
  timeMinuteOther,
  timeDayOne,
  timeDayOther,
} = messages;

/**
 * @typedef {Object} country
 * @property {string} code
 * @property {string} readingDirection
 */
export const getLocaleByCountry = (country: Object = {}) => {
  if (country && country.readingDirection === 'rtl') {
    return 'en';
  }

  return null;
};

export const formatArea = (val: number, intl: ?Object, forcedLocale: ?string = null) => {
  if (forcedLocale) {
    return (
      <IntlProvider locale={forcedLocale}>
        <span>
          <FormattedNumber value={val} options={{ minimumFractionDigits: 0 }} />
        </span>
      </IntlProvider>
    );
  }

  if (intl) {
    return `${intl.formatNumber(val, { minimumFractionDigits: 0 })}${intl.formatMessage(
      areaMetric
    )}`;
  }

  return (
    <span>
      <FormattedNumber value={val} options={{ minimumFractionDigits: 0 }} />
    </span>
  );
};

export const formatTypeId = (val: number, intl: Object, countryDealTypes: Object) =>
  countryDealTypes[val] && intl.formatMessage({ id: countryDealTypes[val] });

export const formatValueToBoolean = (val: any) => {
  if (val) {
    return <FormattedMessage {...yes} />;
  }

  return <FormattedMessage {...no} />;
};

export const formatPrice = (val: number, intl: ?Object, forcedLocale: ?string = null) => {
  if (forcedLocale) {
    return (
      <IntlProvider locale={forcedLocale}>
        <span>
          <FormattedMessage
            {...currencyPrint}
            values={{
              '%s': <FormattedNumber value={val} options={{ minimumFractionDigits: 0 }} />,
            }}
          />
        </span>
      </IntlProvider>
    );
  }

  if (intl) {
    return intl.formatMessage(currencyPrint, {
      '%s': intl.formatNumber(val, { minimumFractionDigits: 0 }),
    });
  }

  return (
    <FormattedMessage
      {...currencyPrint}
      values={{
        '%s': <FormattedNumber value={val} options={{ minimumFractionDigits: 0 }} />,
      }}
    />
  );
};

export const formatMileage = (val: number, intl: Object) =>
  `${intl.formatNumber(val)} ${intl.formatMessage(mileage)}`;

export const sortToText = (val: string, intl: Object) => {
  switch (val) {
    case 'relevance':
      return intl.formatMessage(relevance);
    case 'source_date':
      return intl.formatMessage(dateRecent);
    case 'source_date_asc':
      return intl.formatMessage(dateOldest);
    case 'price_desc':
      return intl.formatMessage(priceHighFirst);
    case 'price':
      return intl.formatMessage(priceLowFirst);
    case 'price_per_meters_desc':
      return intl.formatMessage(priceAreaHighFirst);
    case 'price_per_meters':
      return intl.formatMessage(priceAreaLowFirst);
    default:
      return '';
  }
};

// Evaluates if a parameter exist in a API url or not with te format "/param.value"
export const urlHasParameter = (url: string, parameter: string) => {
  const regex = RegExp(`/${parameter}.`);
  return regex.test(url);
};

// Seeks for a parameter in an API url and replace it for a given value based on the format "/param.value/"
export const replaceUrlParameter = (url: string, parameter: string, value: string) => {
  const regex = RegExp(`\\/${parameter}\\.(.*?)\\/`, 'g');
  return url.replace(regex, `/${parameter}.${value}/`);
};

/**
 * Gets elapsed seconds since de Ad was published and
 * returns it as a translated relative time
 *
 * @example
 *   if secondsAgo < an hour --> returns minutes
 *   if secondsAgo < a day   --> returns hours
 *   if secondsAgo < a month --> returns days
 *   if secondsAgo > a month --> returns "more than 30 days"
 *
 * @param {*} secondsAgo
 * @param {*} intl
 */
export const formatSecondsToRelativeTime = (secondsAgo: number, intl: IntlShape): string => {
  const ONE_MINUTE_IN_SECONDS = 60;
  const ONE_HOUR_IN_SECONDS = 60 * 60;
  const ONE_DAY_IN_SECONDS = 60 * 60 * 24;
  const ONE_MONTH_IN_SECONDS = 60 * 60 * 24 * 30;

  const isLessThanAnHour = secondsAgo < ONE_HOUR_IN_SECONDS;
  const isLessThanADay = secondsAgo < ONE_DAY_IN_SECONDS;
  const isLessThanAMonth = secondsAgo < ONE_MONTH_IN_SECONDS;

  if (isLessThanAnHour) {
    const relativeTime = Math.floor(secondsAgo / ONE_MINUTE_IN_SECONDS);
    const isPlural = relativeTime > 1;
    return intl.formatMessage(isPlural ? timeMinuteOther : timeMinuteOne, { '%d': relativeTime });
  }
  if (isLessThanADay) {
    const relativeTime = Math.floor(secondsAgo / ONE_HOUR_IN_SECONDS);
    const isPlural = relativeTime > 1;
    return intl.formatMessage(isPlural ? timeHourOther : timeHourOne, { '%d': relativeTime });
  }
  if (isLessThanAMonth) {
    const relativeTime = Math.floor(secondsAgo / ONE_DAY_IN_SECONDS);
    const isPlural = relativeTime > 1;
    return intl.formatMessage(isPlural ? timeDayOther : timeDayOne, { '%d': relativeTime });
  }

  return intl.formatMessage(timeSinceSince, { '%s': intl.formatMessage(timeSinceMoreMonth) });
};



// WEBPACK FOOTER //
// ./app/utils/strings.js